import { useEffect, useState } from 'react';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import { useToast } from '../ui/Toast';
import { BuildLocationStatusOptions, ParseDecimalInput, ParseNumberListInput } from './locationFields';
import { NormalizeRack } from './payload';
import { useWarehouseLabels } from './warehouseLabels';

// O contrato do bulk v2 aceita UM set por chamada: status OU peso OU largura.
const BULK_PROPERTIES = ['status', 'max_weight_kg', 'bay_width'];

// Prefixo do rack no wire: 1–2 LETRAS em caixa alta (`A`, `AB`) — nunca número.
const RACK_PREFIX_PATTERN = /^[A-Z]{1,2}$/;

/**
 * Edição em massa v2 (M1 r3 — PATCH /warehouse/locations/bulk): matriz
 * completa `rack × bays[] × levels[] × campo`.
 *
 * - Blocos e andares são MULTI: campo de texto "1,3,5-9" (números e faixas)
 *   ou VAZIO = todos.
 * - Campo a alterar: status (Ativa/Bloqueada/Manutenção) | peso máx | largura
 *   do vão. LARGURA DESABILITA os andares — no r3 a largura é do BLOCO
 *   (wms_blocks): vale para todas as posições dele em todos os andares.
 *
 * Wire: `{rack, bays?: [], levels?: [], set: {...}}` (chave omitida = todos;
 * `rack` é a LETRA do porta-pallet). Toast mostra o `affected` devolvido pelo
 * backend. `rackOptions` são as letras dos racks existentes; `initialRack`
 * pré-seleciona um deles.
 */
const BulkLocationEditModal = ({ isOpen, onClose, onApplied, rackOptions = [], initialRack = '' }) => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    const [rack, setRack] = useState('');
    const [baysText, setBaysText] = useState('');
    const [levelsText, setLevelsText] = useState('');
    const [property, setProperty] = useState('status');
    const [statusValue, setStatusValue] = useState('ACTIVE');
    const [numberValue, setNumberValue] = useState('');
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setRack(NormalizeRack(initialRack) ?? (rackOptions.length > 0 ? String(rackOptions[0]) : ''));
            setBaysText('');
            setLevelsText('');
            setProperty('status');
            setStatusValue('ACTIVE');
            setNumberValue('');
            setFormError(null);
        }
        // `rackOptions` fora das deps de propósito: o reset é do MOMENTO de
        // abrir; a lista chegando depois não deve limpar o que o usuário mexeu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialRack]);

    const closeModal = () => {
        if (!saving) {
            onClose();
        }
    };

    const propertyLabels = {
        status: label('wms.field.status'),
        max_weight_kg: label('wms.field.max_weight_kg'),
        bay_width: label('wms.field.bay_width'),
    };

    // Largura é POR BLOCO: andares não participam do recorte.
    const levelsDisabled = property === 'bay_width';

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError(null);

        const rackPrefix = NormalizeRack(rack);
        if (rackPrefix === null || !RACK_PREFIX_PATTERN.test(rackPrefix)) {
            setFormError({ field: 'rack', message: label('wms.common.required') });
            return;
        }
        const bays = ParseNumberListInput(baysText);
        if (Number.isNaN(bays)) {
            setFormError({ field: 'bays', message: label('wms.bulk.invalid_list') });
            return;
        }
        const levels = levelsDisabled ? null : ParseNumberListInput(levelsText);
        if (Number.isNaN(levels)) {
            setFormError({ field: 'levels', message: label('wms.bulk.invalid_list') });
            return;
        }

        let set;
        if (property === 'status') {
            set = { status: statusValue };
        } else {
            const value = ParseDecimalInput(numberValue);
            if (value === null || Number.isNaN(value) || value <= 0) {
                setFormError({ field: 'value', message: 'Informe um número maior que zero.' });
                return;
            }
            set = property === 'max_weight_kg' ? { max_weight_kg: value } : { bay_width: value };
        }

        const payload = {
            rack: rackPrefix,
            ...(bays !== null ? { bays } : {}),
            ...(levels !== null ? { levels } : {}),
            set,
        };

        setSaving(true);
        try {
            const data = await api.bulkUpdateWarehouseLocations(payload);
            const affected = Number(data?.affected ?? 0);
            toast.success(`${FormatInt(affected)} localizações atualizadas`);
            onApplied?.();
            onClose();
        } catch (error) {
            if (error?.code === 'NETWORK_ERROR') {
                setFormError({ message: label('wms.collector.error_network') });
            } else {
                setFormError({ message: error?.message || 'Erro ao salvar' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={label('wms.bulk.title')} size="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.bulk.description_v2')}</p>

                <Select
                    label={label('wms.field.rack')}
                    value={rack}
                    onChange={(event) => setRack(event.target.value)}
                    errorMessage={formError?.field === 'rack' ? formError.message : undefined}
                    disabled={saving || rackOptions.length === 0}
                >
                    {rackOptions.length === 0 ? <option value="">{label('wms.locations.no_racks')}</option> : null}
                    {rackOptions.map((option) => (
                        <option key={option} value={String(option)}>
                            {`${label('wms.field.rack')} ${option}`}
                        </option>
                    ))}
                </Select>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Input
                            label={label('wms.generator.blocks')}
                            placeholder="ex.: 1,3,5-9"
                            value={baysText}
                            onChange={(event) => setBaysText(event.target.value)}
                            errorMessage={formError?.field === 'bays' ? formError.message : undefined}
                            disabled={saving}
                            autoComplete="off"
                        />
                        {formError?.field !== 'bays' ? (
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.bulk.blocks_help')}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <Input
                            label={label('wms.generator.levels')}
                            placeholder="ex.: 1,3-5"
                            value={levelsText}
                            onChange={(event) => setLevelsText(event.target.value)}
                            errorMessage={formError?.field === 'levels' ? formError.message : undefined}
                            disabled={saving || levelsDisabled}
                            autoComplete="off"
                        />
                        {formError?.field !== 'levels' ? (
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                {levelsDisabled ? label('wms.bulk.width_is_per_block') : label('wms.bulk.levels_help')}
                            </p>
                        ) : null}
                    </div>
                </div>

                <fieldset>
                    <legend className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300">
                        {label('wms.bulk.property')}
                    </legend>
                    <div className="flex flex-wrap gap-4">
                        {BULK_PROPERTIES.map((option) => (
                            <label key={option} className="flex items-center gap-2 text-sm text-black dark:text-white">
                                <input
                                    type="radio"
                                    name="bulk-property"
                                    value={option}
                                    checked={property === option}
                                    onChange={() => {
                                        setProperty(option);
                                        setFormError(null);
                                    }}
                                    disabled={saving}
                                    className="h-4 w-4"
                                />
                                {propertyLabels[option]}
                            </label>
                        ))}
                    </div>
                </fieldset>

                {property === 'status' ? (
                    <Select
                        label={label('wms.field.status')}
                        value={statusValue}
                        onChange={(event) => setStatusValue(event.target.value)}
                        disabled={saving}
                    >
                        {BuildLocationStatusOptions(label).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <Input
                        label={propertyLabels[property]}
                        inputMode="decimal"
                        placeholder={property === 'bay_width' ? 'ex.: 2,50' : 'ex.: 1000'}
                        value={numberValue}
                        onChange={(event) => setNumberValue(event.target.value)}
                        errorMessage={formError?.field === 'value' ? formError.message : undefined}
                        disabled={saving}
                        autoComplete="off"
                    />
                )}

                {formError && !formError.field ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {formError.message}
                    </p>
                ) : null}

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button type="submit" loading={saving} className="flex-1">
                        {label('wms.bulk.apply')}
                    </Button>
                    <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                        {label('wms.common.cancel')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default BulkLocationEditModal;
